import { LanguageType } from '@/models/LanguageType';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { ProgramClass } from '@/models/ProgramClass';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { isWorkshopConfigured } from '@/services/WorkshopService';
import { isBefore } from 'date-fns';
import _ from 'underscore';
import Constants from '../Constants';
import { CourseEnrollment } from '../models/CourseEnrollment';
import Item from '../models/Item';
import List, { ListRequest } from '../models/List';
import { Module } from '../models/Module';
import { store } from '../store';
import { Favorite } from '../store/favorite/types';
import { LocaleData } from '../translations/LocaleData';
import { formatDate, isNull, momentWithTimeZone } from './UtilService';

export class CourseService {
    isCourseContent = (): boolean => Object.keys(store.getState().lists.currentData).length > 0;

    getLessonStatusIsCompleted = (lessonEnrollments: LessonEnrollment[], lessonId: number | undefined): boolean => {
        if (lessonId) {
            const tmpLessonEnrollment: LessonEnrollment = lessonEnrollments.find(
                (lessonEnrollment: LessonEnrollment) => lessonEnrollment.lesson.id === lessonId,
            ) as LessonEnrollment;

            return tmpLessonEnrollment && tmpLessonEnrollment.status === 'COMPLETED';
        }

        return false;
    };

    getWorkshopDateWithEventPeriod = (startDate: Date, endDate: Date, strings: LocaleData): string | undefined => {
        if (startDate && endDate) {
            const date = momentWithTimeZone(startDate, strings._LANGUAGE);
            const startTime = momentWithTimeZone(startDate, strings._LANGUAGE, false, true);
            const endTime = momentWithTimeZone(endDate, strings._LANGUAGE, false, true);

            return `${date}, ${strings.FROM.toLocaleLowerCase()} ${startTime} ${strings.TO.toLocaleLowerCase()} ${endTime} BRT`;
        }
        return undefined;
    };

    getFavoriteEntityListAsIdsString = (favoriteList: Favorite[], entityType: string): string => {
        const resultList = favoriteList.filter((item: Favorite) => item.contentType === entityType);
        if (resultList.length > 0) {
            return resultList.map((favorite: Favorite) => favorite.contentId).join(',');
        }
        return '';
    };

    isFavorited = (courseId: number, favoriteList: Favorite[]): boolean => {
        const resultList = favoriteList.filter((item: Favorite) => item.contentType === Constants.entityTypes.COURSE && item.contentId === courseId);
        return resultList.length > 0;
    };

    isContentInCourse = (course: List, content: Item): boolean => {
        const list =
            course.lessons &&
            course.lessons.filter((e) => {
                return e.content.id === content.id;
            });

        return list !== undefined && list.length > 0;
    };

    getModuleIdsForRequest = (modules: Module[], moduleIdsForRequest: number[]): string => {
        return modules
            .filter((module: Module) => moduleIdsForRequest.find((moduleId: number) => module.id === moduleId))
            .map((module: Module) => module.id)
            .join(',');
    };

    getLanguageRendering = (type: LanguageType, strings: LocaleData) => {
        switch (type) {
            case 'pt':
                return strings.PORTUGUESE;
            case 'en':
                return strings.ENGLISH;
            default:
                return '';
        }
    };

    formatWorkload = (workload: number, strings: LocaleData, agregate: boolean = false): string => {
        if (workload >= 60) {
            const hours = workload / 60;
            const rhours = Math.floor(hours);
            const minutes = Math.round((hours - rhours) * 60);

            if (agregate) {
                const h = `${rhours > 9 ? rhours : `0${rhours}`}`;
                const min = `${minutes > 9 ? minutes : `0${minutes}`}`;
                return `${h}:${min} ${strings.HOURS}`;
            }
            if (minutes > 0) {
                return `${rhours}${strings.HOURS} ${minutes}${strings.MINUTES}`;
            } else {
                return `${rhours} ${strings.HOURS}`;
            }
        } else {
            return `${workload} ${strings.MINUTES}`;
        }
    };

    isExpired = (courseEnrollment: CourseEnrollment | undefined, programClass: ProgramClass | undefined) => {
        const doneStatus = [
            Constants.courseEnrollmentStatus.FAILED,
            Constants.courseEnrollmentStatus.COMPLETED,
            Constants.courseEnrollmentStatus.PASSED,
        ];

        if (courseEnrollment && doneStatus.includes(courseEnrollment.status)) return false;

        return programClass?.isExpired;
    };

    getCourseStatusLabel = (
        course: List,
        courseEnrollment: CourseEnrollment,
        strings: LocaleData,
        programClass: ProgramClass | undefined,
    ): string => {
        if (!!course && programClass && course?.type === 'WORKSHOP' && !isWorkshopConfigured(programClass.workshops, course?.id)) {
            return strings.SOON;
        }

        if (isNull(courseEnrollment)) {
            return strings.COURSE_NOT_STARTED;
        }

        switch (courseEnrollment.status) {
            case Constants.courseEnrollmentStatus.NOT_STARTED:
                return strings.COURSE_NOT_STARTED;
            case Constants.courseEnrollmentStatus.STARTED:
                return strings.COURSE_STARTED;
            case Constants.courseEnrollmentStatus.COMPLETED:
                return strings.COURSE_COMPLETED;
            case Constants.courseEnrollmentStatus.PASSED:
                return strings.COURSE_PASSED;
            case Constants.courseEnrollmentStatus.FAILED:
                return strings.COURSE_FAILED;
            case Constants.courseEnrollmentStatus.BLOCKED:
                return strings.COURSE_BLOCKED;
            default:
                return strings.COURSE_BLOCKED;
        }
    };

    getCourseLMSStatusLabel = (courseEnrollment: CourseEnrollment, programClass: ProgramClass, strings: LocaleData): string => {
        if (courseEnrollment === null) {
            return strings.COURSE_NOT_STARTED;
        }

        switch (courseEnrollment.status) {
            case Constants.courseEnrollmentStatus.NOT_STARTED:
                return programClass.finishDate !== null && this.courseIsExpired(programClass.finishDateAsString)
                    ? strings.COURSE_EXPIRED
                    : strings.COURSE_NOT_STARTED;
            case Constants.courseEnrollmentStatus.STARTED:
                return programClass.finishDate !== null && this.courseIsExpired(programClass.finishDateAsString)
                    ? strings.COURSE_EXPIRED
                    : strings.COURSE_STARTED;
            case Constants.courseEnrollmentStatus.COMPLETED:
                return strings.COURSE_COMPLETED;
            case Constants.courseEnrollmentStatus.PASSED:
                return strings.COURSE_PASSED;
            case Constants.courseEnrollmentStatus.FAILED:
                return strings.COURSE_FAILED;
            case Constants.courseEnrollmentStatus.BLOCKED:
                return strings.COURSE_BLOCKED;
            default:
                return strings.COURSE_BLOCKED;
        }
    };

    courseIsExpired = (finishDateAsString: string): boolean =>
        finishDateAsString !== null && isBefore((finishDateAsString as unknown) as Date, Date.now());

    getLessonEnrollmnetByLessonId = (lessonEnrollments: LessonEnrollment[], lessonId: number) =>
        lessonEnrollments.find((lessonEnrollment: LessonEnrollment) => Number(lessonEnrollment.lesson.id) === Number(lessonId));

    getLessonStatusLabel = (lessonEnrollment: LessonEnrollment, strings: LocaleData): string => {
        if (lessonEnrollment === null) {
            return strings.COURSE_NOT_STARTED;
        }
        switch (lessonEnrollment.status) {
            case Constants.lessonEnrollmentStatus.COMPLETED:
                return strings.CONCLUDED;
            case Constants.lessonEnrollmentStatus.FAILED:
                return strings.FAILED;
            case Constants.lessonEnrollmentStatus.INCOMPLETE:
                return strings.INCOMPLETE;
            case Constants.lessonEnrollmentStatus.NOT_ATTEMPTED:
                return strings.NOT_ATTEMPTED;
            default:
                return '';
        }
    };

    getTotalRating = (course: List): number | string => {
        return (course.totalRating > 0 && course.rating?.toFixed(1)) || '-';
    };

    formatWorkshopMeetingDate = (workshopMeeting: WorkshopMeeting, daysToMeeting: number = 15): string => {
        const strings: LocaleData = store.getState().locale.data;

        if (workshopMeeting.daysToStart === 0) {
            return strings.TODAY;
        } else if (workshopMeeting.daysToStart <= daysToMeeting) {
            return `${strings.IN} ${workshopMeeting.daysToStart} ${
                workshopMeeting.daysToStart === 1 ? strings.DAY.toLocaleLowerCase() : strings.DAYS.toLocaleLowerCase()
            }`;
        }

        return `${strings.AT} ${formatDate(workshopMeeting.startDate, strings._LANGUAGE)}`;
    };

    getQueryParams = ({
        applicationId,
        orgId,
        name,
        status,
        onlyHasProgramClass,
        orderBy,
        locale,
        moduleId,
        offset,
        limit,
        type,
        notInProgramId,
        categoryIds,
        withLessonAmount,
        withClassAmount,
    }: ListRequest) => {
        let queryParams = '?';

        if (_.isNumber(applicationId)) {
            queryParams += `applicationId=${applicationId}`;
        }

        if (withLessonAmount !== undefined) {
            queryParams += `&withLessonAmount=${withLessonAmount}`;
        }

        if (withClassAmount !== undefined) {
            queryParams += `&withClassAmount=${withClassAmount}`;
        }

        if (_.isNumber(orgId)) {
            queryParams += `&organizationId=${orgId}`;
        }

        if (onlyHasProgramClass !== undefined) {
            queryParams += `&onlyHasProgramClass=${onlyHasProgramClass}`;
        }

        if (!_.isEmpty(name)) {
            queryParams += `&name=${name}`;
        }
        if (status !== undefined) {
            queryParams += `&status=${status}`;
        }
        if (moduleId !== undefined) {
            queryParams += `&moduleId=${moduleId}`;
        }
        if (!_.isEmpty(orderBy)) {
            queryParams += `&orderBy=${orderBy}`;
        }
        if (locale !== undefined) {
            queryParams += `&locale=${locale}`;
        }

        if (_.isNumber(offset)) {
            queryParams += `&offset=${offset}`;
        }
        if (_.isNumber(limit)) {
            queryParams += `&limit=${limit}`;
        }

        if (notInProgramId !== undefined) {
            queryParams += `&notInProgramId=${notInProgramId}`;
        }
        if (categoryIds !== undefined) {
            queryParams += `&categoryIds=${categoryIds}`;
        }
        if (type !== undefined) {
            queryParams += `&type=${type}`;
        }

        return queryParams;
    };
}
export const courseService = new CourseService();

export const articleTpl: string = `
<!doctype html>
<html>
    <head>
        <meta charset="UTF-8">
        <meta http-equiv="x-ua-compatible" content="IE=edge">
        <meta name="viewport" content="initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no">
        <meta name="apple-mobile-web-app-capable" content="yes">
        <meta name="apple-touch-fullscreen" content="yes">
        <link rel="stylesheet" href="https://flow.afferolab.net/static/assets/flow/css/bootstrap.min.css" />
        <link rel="stylesheet" href="https://flow.afferolab.net/static/assets/flow/css/afferolab_flow.css" />
    </head>
    <div class="container">{0}</div>
</html>
`;
